<script lang="ts" setup>
import StatusSelector, {
  type Option,
} from "@/components/common/StatusSelector/StatusSelector.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { STATUS } from "@/stores/constants";
import type { Status } from "@/api/employeeMovements";
import type { Visit, VisitWithFields } from "@/api/visits";

export interface Props {
  movement?: Visit;
  visit?: VisitWithFields;
  disabled?: boolean;
  size?: "small" | "default";
  loading?: boolean;
}

const item = computed(() => {
  if (props.movement) return props.movement;

  if (props.visit) {
    return {
      review_status: props.visit.reviewStatus as unknown as STATUS,
      visit_fact_at_tz: props.visit.startedAt?.toISOString(),
      visit_fact_to_tz: props.visit.finishedAt?.toISOString(),
    };
  }

  return undefined;
});

const props = withDefaults(defineProps<Props>(), {
  size: "default",
  loading: false,
  onlySignOut: false,
});

const emit = defineEmits<{
  (e: "change", status: Status): void;
}>();

const { t } = useI18n();

const currentStatus = computed(() => {
  if (item.value?.review_status === STATUS.PENDING) {
    return STATUS.PENDING;
  }

  if (
    item.value?.review_status &&
    [STATUS.DENY, STATUS.LAPSED].includes(item.value.review_status)
  ) {
    return STATUS.DENIED;
  }

  if (item.value?.visit_fact_at_tz && !item.value?.visit_fact_to_tz) {
    return STATUS.IN;
  }

  return STATUS.OUT;
});

const isPending = computed(() => currentStatus.value === STATUS.PENDING);

const statusOptions = computed<(Option & { applicable: boolean })[]>(() => [
  {
    label: t("timelines.statuses.in"),
    value: STATUS.IN,
    applicable: !isPending.value,
  },
  {
    label: t("timelines.statuses.out"),
    value: STATUS.OUT,
    applicable: !isPending.value,
  },
  {
    label: t("timelines.statuses.approve"),
    value: STATUS.APPROVE,
    applicable: isPending.value,
  },
  {
    label: t("timelines.statuses.reject"),
    value: STATUS.REJECT,
    applicable: isPending.value,
  },
]);

const options = computed<Option[]>(() =>
  statusOptions.value.filter((option) => option.applicable)
);

const isStatic = computed(() =>
  [STATUS.OUT, STATUS.DENIED].includes(currentStatus.value)
);
</script>

<template>
  <StatusSelector
    :current-status="currentStatus"
    :options="options"
    :static="isStatic"
    :loading="loading"
    :disabled="disabled"
    :size="size"
    @change="emit('change', $event)"
  />
</template>
